import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Heading from '@jpp/atoms/Heading';
import Loading from '@jpp/atoms/Loading';
import useIsMounted from '@jpp/hooks/useIsMounted';
import BlogCard from '@jpp/molecules/BlogCard';
import Pagination from '@jpp/organisms/Pagination';
import RelatedPosts from '@jpp/organisms/RelatedPosts';
import Container from '@jpp/shared/Grid/Container';
import Flex from '@jpp/shared/Grid/Flex';
import Row from '@jpp/shared/Grid/Row';
import Section from '@jpp/shared/Grid/Section';
import { IReduxState } from 'common/redux/createStore';
import { IPageStoreState } from 'common/redux/page/reducer';
import { TPostsStoreState } from 'common/redux/posts/reducer';
import {
  getAllPosts,
  getTotalPages,
  getTotalPosts,
} from 'common/redux/posts/selectors';
import { clearPosts, getPosts } from 'common/redux/rootActions';
import { EPageType, ETheme } from 'common/typings/enums';

import './Blog.scss';

export interface IBlogPage {
  className?: string;
  page: IPageStoreState;
  posts: TPostsStoreState;
}

const ROOT_CLASSNAME = 'Blog';
const AMOUNT_OF_FEATURE_POSTS = 3;

const getPostsFromState = async (dispatch, currentPage) => {
  await dispatch(clearPosts());
  await dispatch(getPosts(currentPage));
};

const Blog: React.FunctionComponent<IBlogPage> = ({
  className,
  page,
  ...rest
}) => {
  const {
    totalItems,
    totalPages,
    posts: postsFromState,
  } = useSelector((state: IReduxState) => ({
    posts: getAllPosts(state),
    totalPages: getTotalPages(state),
    totalItems: getTotalPosts(state),
  }));
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const posts = postsFromState || rest.posts || [];

  const dispatch = useDispatch();
  const { asPath } = useRouter();
  const queryPage = asPath.split('?')[1]?.split('=')[1];
  const currentPage = queryPage
    ? parseInt(Array.isArray(queryPage) ? queryPage[0] : queryPage, 10)
    : 1;
  const { post_title } = page;
  const title = post_title || page?.title;
  const firstPost = posts[0];
  const twoAndThreePosts = posts.slice(1, AMOUNT_OF_FEATURE_POSTS);
  const otherPosts = posts.slice(AMOUNT_OF_FEATURE_POSTS, posts.length);

  useEffect(() => {
    setIsLoading(true);

    getPostsFromState(dispatch, currentPage).finally(() => {
      setIsLoading(false);
    });

    return () => {
      if (isMounted.current) {
        setIsLoading(false);
      }
    };
  }, [currentPage]);

  return (
    <Section className={classNames(ROOT_CLASSNAME, className)}>
      <Container className={`${ROOT_CLASSNAME}__container`}>
        <Row>
          <Flex>
            <Heading
              priority={1}
              className={`${ROOT_CLASSNAME}__heading`}
              children={title}
            />
          </Flex>
        </Row>

        {isLoading ? (
          <div className={`${ROOT_CLASSNAME}__loading-wrapper`}>
            <Loading
              theme={ETheme.Brand}
              className={`${ROOT_CLASSNAME}__loading`}
            />
          </div>
        ) : (
          <>
            <Row>
              <Flex
                className={`${ROOT_CLASSNAME}__featured-posts-wrapper`}
                colLg={3}
                colMd={6}
              >
                {twoAndThreePosts.map((post, index) => {
                  if (!post) {
                    return null;
                  }

                  return (
                    <div
                      key={`${post.title} ${index}`}
                      className={classNames(
                        `${ROOT_CLASSNAME}__featured-post-wrapper`,
                        `${ROOT_CLASSNAME}__featured-post-wrapper--${index + 1}`
                      )}
                    >
                      <BlogCard
                        className={`${ROOT_CLASSNAME}__featured-post`}
                        post={post}
                      />
                    </div>
                  );
                })}
              </Flex>

              <Flex
                className={`${ROOT_CLASSNAME}__featured-posts-wrapper`}
                colLg={6}
                colMd={12}
              >
                <div
                  className={classNames(
                    `${ROOT_CLASSNAME}__featured-post-wrapper`,
                    `${ROOT_CLASSNAME}__featured-post-wrapper--main`
                  )}
                >
                  <BlogCard
                    className={`${ROOT_CLASSNAME}__featured-post`}
                    post={firstPost}
                    featured={true}
                  />
                </div>
              </Flex>

              {otherPosts && otherPosts.length > 0 && (
                <Flex
                  className={`${ROOT_CLASSNAME}__related-posts-wrapper`}
                  colLg={3}
                  colMd={6}
                >
                  <RelatedPosts posts={otherPosts} />
                </Flex>
              )}
            </Row>

            <Row>
              <Flex>
                <Pagination
                  className={`${ROOT_CLASSNAME}__pagination`}
                  currentPage={currentPage}
                  totalItems={totalItems}
                  totalPages={totalPages}
                  hrefBase={`/${EPageType.Blog}`}
                />
              </Flex>
            </Row>
          </>
        )}
      </Container>
    </Section>
  );
};

export default Blog;
